import { useEffect, useState } from "react";
import { KeyRound, Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";

function StatMini({ label, value, accent }) {
    return (
        <div className="card-elevated p-4">
            <div className="text-[11.5px] text-muted-foreground">{label}</div>
            <div className="text-[22px] font-bold leading-tight tracking-tight mt-1">{value}</div>
            <div className={`h-1 w-16 rounded-full mt-3 ${accent}`} />
        </div>
    );
}

export default function PermissionsCatalog() {
    const canView = useCan("permissions.view");
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!canView) { setLoading(false); return; }
        (async () => {
            try {
                const { data } = await api.get("/permissions/grouped");
                setGroups(data.groups || []);
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Yüklenemedi.");
            } finally {
                setLoading(false);
            }
        })();
    }, [canView]);

    if (!canView) return <Forbidden />;

    const filteredGroups = groups.map((g) => {
        if (!search) return g;
        const q = search.toLowerCase();
        return { ...g, permissions: g.permissions.filter((p) =>
            p.name.toLowerCase().includes(q)
            || p.code.toLowerCase().includes(q)
            || g.category_label.toLowerCase().includes(q)
        ) };
    }).filter((g) => g.permissions.length > 0);

    const totalPerms = groups.reduce((sum, g) => sum + g.permissions.length, 0);

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <h1 className="text-[26px] font-bold tracking-tight">{tr.permissions.title}</h1>
                <p className="text-[13.5px] text-muted-foreground mt-1">{tr.permissions.subtitle}</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatMini label={tr.permissions.totalPermissions} value={totalPerms} accent="bg-[hsl(var(--brand))]" />
                <StatMini label={tr.permissions.totalCategories} value={groups.length} accent="bg-[hsl(var(--info))]" />
                <StatMini label="Aksiyon Türleri" value={11} accent="bg-[hsl(var(--success))]" />
            </div>

            <div className="card-elevated p-4 flex items-center gap-2">
                <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Yetki ara (isim veya kod)…"
                        className="h-10 w-full rounded-xl border border-border bg-background pl-9 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                    />
                    {search && (
                        <button onClick={() => setSearch("")} className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="py-16 text-center text-muted-foreground">{tr.common.loading}</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredGroups.map((g) => (
                        <div key={g.category} className="card-elevated p-5">
                            <div className="mb-3 flex items-center gap-2">
                                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]">
                                    <KeyRound className="h-4 w-4" />
                                </div>
                                <div>
                                    <div className="text-[14.5px] font-semibold">{g.category_label}</div>
                                    <div className="text-[11px] text-muted-foreground">{g.permissions.length} yetki</div>
                                </div>
                            </div>
                            <ul className="divide-y divide-border">
                                {g.permissions.map((p) => (
                                    <li key={p.code} className="flex items-center justify-between py-2 gap-3">
                                        <div className="min-w-0">
                                            <div className="text-[12.5px] font-medium truncate">{p.name}</div>
                                            <div className="text-[10.5px] text-muted-foreground font-mono truncate">{p.code}</div>
                                        </div>
                                        <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-[10.5px] font-semibold uppercase text-muted-foreground shrink-0">
                                            {p.action_label}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
